import { useState } from 'react'
import { useRouter } from 'next/router'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { AppRoutes } from '@/config/routes'
import useWallet from '@/hooks/wallets/useWallet'
import { useCurrentChain } from '@/hooks/useChains'
import useIsWrongChain from '@/hooks/useIsWrongChain'
import { createWeb3, useWeb3ReadOnly } from '@/hooks/wallets/web3'
import EthHashInfo from '@/components/common/EthHashInfo'
import ChainSwitcher from '@/components/common/ChainSwitcher'
import { parsePrefixedAddress, formatPrefixedAddress } from '@/utils/addresses'
import {
  buildReplayCalldata,
  decodeSetupData,
  fetchSafeCreation,
  verifyReplayOnChain,
  type DecodedSetup,
  type ReplayVerification,
  type SafeCreationInfo,
} from './logic'

const RecreateSafe = () => {
  const router = useRouter()
  const wallet = useWallet()
  const chain = useCurrentChain()
  const isWrongChain = useIsWrongChain()
  const web3ReadOnly = useWeb3ReadOnly()

  const [input, setInput] = useState('')
  const [safeAddress, setSafeAddress] = useState('')
  const [creation, setCreation] = useState<SafeCreationInfo>()
  const [setup, setSetup] = useState<DecodedSetup>()
  const [verification, setVerification] = useState<ReplayVerification>()
  const [status, setStatus] = useState<'idle' | 'looking' | 'deploying'>('idle')
  const [error, setError] = useState('')
  const [txHash, setTxHash] = useState('')

  const onLookup = async () => {
    setError('')
    setCreation(undefined)
    setSetup(undefined)
    setVerification(undefined)
    setTxHash('')

    let address: string
    let prefix: string | undefined
    try {
      ;({ address, prefix } = parsePrefixedAddress(input.trim()))
    } catch (e) {
      setError('Invalid address')
      return
    }
    setSafeAddress(address)
    setStatus('looking')

    try {
      const found = await fetchSafeCreation(address, prefix)
      if (!found) {
        setError('No creation transaction found for this address on any known network')
        return
      }
      setCreation(found)
      setSetup(decodeSetupData(found.setupData))

      if (web3ReadOnly) {
        setVerification(await verifyReplayOnChain(web3ReadOnly, found, address))
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setStatus('idle')
    }
  }

  const onDeploy = async () => {
    if (!wallet || !creation) return
    setError('')
    setStatus('deploying')

    try {
      const signer = createWeb3(wallet.provider).getSigner()
      const tx = await signer.sendTransaction({
        to: creation.factoryAddress,
        data: buildReplayCalldata(creation),
        value: 0,
      })
      setTxHash(tx.hash)
      await tx.wait()

      router.push({
        pathname: AppRoutes.balances.index,
        query: { safe: formatPrefixedAddress(safeAddress, chain?.shortName) },
      })
    } catch (e) {
      setError((e as Error & { reason?: string }).reason || (e as Error).message)
    } finally {
      setStatus('idle')
    }
  }

  return (
    <Container>
      <Grid container columnSpacing={3} justifyContent="center" mt={[2, null, 7]}>
        <Grid item xs={12} md={8}>
          <Typography variant="h2" pb={2}>
            Recreate an existing Safe
          </Typography>
          <Typography color="text.secondary" pb={3}>
            Deploy a Safe you already own on another network at the same address on {chain?.chainName || 'this network'}
            . The original creation transaction is looked up on the official Safe networks and replayed here byte for
            byte, so the address, the initial owners and the threshold all stay the same.
          </Typography>

          <Card sx={{ p: 3 }}>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Safe address on another network"
                placeholder="0x... or eth:0x..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <Button variant="contained" onClick={onLookup} disabled={!input.trim() || status !== 'idle'}>
                {status === 'looking' ? <CircularProgress size={20} /> : 'Look up'}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {creation && setup && (
              <Box mt={3}>
                <Typography variant="h5" pb={1}>
                  Original creation found on {creation.sourceName}
                </Typography>
                <Typography variant="body2" color="text.secondary" pb={2}>
                  Safe v1.4.1 deployment, salt nonce {creation.saltNonce}, threshold {setup.threshold} out of{' '}
                  {setup.owners.length} owner(s):
                </Typography>
                {setup.owners.map((owner) => (
                  <Box key={owner} py={0.5}>
                    <EthHashInfo address={owner} showCopyButton hasExplorer shortAddress={false} />
                  </Box>
                ))}

                {verification && (
                  <>
                    <Typography variant="h5" pt={3} pb={1}>
                      Checks on {chain?.chainName}
                    </Typography>
                    <List dense>
                      {verification.checks.map((check) => (
                        <ListItem key={check.label} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {check.ok ? (
                              <CheckCircleOutlineIcon color="success" fontSize="small" />
                            ) : (
                              <HighlightOffIcon color="error" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText primary={check.label} />
                        </ListItem>
                      ))}
                    </List>

                    {verification.ok ? (
                      <Box display="flex" gap={2} alignItems="center" mt={2}>
                        {isWrongChain && <ChainSwitcher />}
                        <Button
                          variant="contained"
                          onClick={onDeploy}
                          disabled={!wallet || isWrongChain || status !== 'idle'}
                        >
                          {status === 'deploying' ? <CircularProgress size={20} /> : `Deploy at the same address`}
                        </Button>
                        {!wallet && <Typography variant="body2">Connect a wallet to deploy</Typography>}
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        This Safe cannot be recreated at the same address on {chain?.chainName}: some of the original
                        deployment contracts are missing here or the address is already taken.
                      </Alert>
                    )}
                  </>
                )}

                {txHash && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Transaction sent: {txHash}. Waiting for confirmation...
                  </Alert>
                )}
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default RecreateSafe
